import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout';
import api from '../../utils/api';
import { useModal } from '../../context/ModalContext';
import { 
  FiUsers, 
  FiDollarSign, 
  FiTrendingUp, 
  FiPlus, 
  FiEdit, 
  FiEye,
  FiSearch,
  FiFilter,
  FiBriefcase,
  FiShoppingBag,
  FiUserCheck,
  FiAlertTriangle,
  FiCalendar,
  FiFileText
} from 'react-icons/fi';

function OwnerDashboard() {
  const { alert, confirm } = useModal();
  const navigate = useNavigate();
  const [tenants, setTenants] = useState([]);
  const [stats, setStats] = useState({
    totalTenants: 0,
    activeTenants: 0,
    totalUsers: 0,
    monthlyRevenue: 0,
    yearlyRevenue: 0,
    totalRevenue: 0
  });
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showModulesModal, setShowModulesModal] = useState(false);
  const [showBillingModal, setShowBillingModal] = useState(false);
  const [showNotesModal, setShowNotesModal] = useState(false);
  const [availableModules, setAvailableModules] = useState([]);
  const [attentionDays, setAttentionDays] = useState(5);
  const [billingAttention, setBillingAttention] = useState([]);

  // Form states
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    email: '',
    phone: '',
    adminName: '',
    adminEmail: '',
    adminPassword: '',
    planName: '',
    planPrice: '',
    billingCycle: 'monthly',
    planStatus: 'active',
    subscriptionStartsAt: '',
    notes: '',
    modules: [],
    maxStaffLogins: ''
  });

  useEffect(() => {
    fetchStats();
    fetchTenants();
    fetchAvailableModules();
    fetchBillingAttention(5);
  }, []);

  useEffect(() => {
    fetchBillingAttention(attentionDays);
  }, [attentionDays]);

  const fetchStats = async () => {
    try {
      const response = await api.get('/api/owner/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const fetchTenants = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/owner/tenants');
      setTenants(response.data.tenants || []);
    } catch (error) {
      console.error('Failed to fetch tenants:', error);
      await alert('Failed to load tenants', 'Error', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailableModules = async () => {
    try {
      const response = await api.get('/api/owner/modules');
      setAvailableModules(response.data.modules || []);
    } catch (error) {
      console.error('Failed to fetch modules:', error);
    }
  };

  const fetchBillingAttention = async (days = 5) => {
    try {
      const response = await api.get(`/api/owner/billing-attention?days=${days}`);
      setBillingAttention(response.data?.attention || []);
    } catch (error) {
      console.error('Failed to fetch billing attention list:', error);
      setBillingAttention([]);
    }
  };

  const handleCreateTenant = async () => {
    try {
      if (!formData.name || !formData.slug || !formData.adminName || !formData.adminEmail || !formData.adminPassword || !formData.maxStaffLogins) {
        await alert('Please fill in all required fields including Max Staff Logins', 'Validation Error', 'warning');
        return;
      }

      const response = await api.post('/api/owner/tenants', formData);
      await alert('Tenant created successfully!', 'Success', 'success');
      setShowCreateModal(false);
      resetForm();
      fetchTenants();
      fetchStats();
    } catch (error) {
      await alert(error.response?.data?.error || 'Failed to create tenant', 'Error', 'error');
    }
  };

  const handleUpdateTenant = async () => {
    try {
      if (!selectedTenant) return;

      await api.put(`/api/owner/tenants/${selectedTenant.id}`, {
        name: formData.name,
        slug: formData.slug,
        email: formData.email,
        phone: formData.phone,
        notes: formData.notes,
        is_active: formData.is_active,
        maxStaffLogins: formData.maxStaffLogins
      });

      await alert('Tenant updated successfully!', 'Success', 'success');
      setShowEditModal(false);
      resetForm();
      fetchTenants();
    } catch (error) {
      await alert(error.response?.data?.error || 'Failed to update tenant', 'Error', 'error');
    }
  };

  const handleUpdateModules = async () => {
    try {
      if (!selectedTenant) return;

      await api.put(`/api/owner/tenants/${selectedTenant.id}/modules`, {
        modules: formData.modules
      });

      await alert('Modules updated successfully!', 'Success', 'success');
      setShowModulesModal(false);
      fetchTenants();
    } catch (error) {
      await alert(error.response?.data?.error || 'Failed to update modules', 'Error', 'error');
    }
  };

  const handleUpdatePlan = async () => {
    try {
      if (!selectedTenant) return;

      await api.put(`/api/owner/tenants/${selectedTenant.id}/plan`, {
        planName: formData.planName,
        planPrice: formData.planPrice,
        billingCycle: formData.billingCycle,
        status: formData.planStatus,
        subscriptionStartsAt: formData.subscriptionStartsAt || null
      });

      await alert('Plan updated successfully!', 'Success', 'success');
      setShowBillingModal(false);
      resetForm();
      fetchTenants();
      fetchStats();
      fetchBillingAttention(attentionDays);
    } catch (error) {
      await alert(error.response?.data?.error || 'Failed to update plan', 'Error', 'error');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      slug: '',
      email: '',
      phone: '',
      adminName: '',
      adminEmail: '',
      adminPassword: '',
      planName: '',
      planPrice: '',
      billingCycle: 'monthly',
      planStatus: 'active',
      subscriptionStartsAt: '',
      notes: '',
      modules: [],
      maxStaffLogins: ''
    });
    setSelectedTenant(null);
  };

  const toDateInputValue = (value) => {
    if (!value) return '';
    const normalized = typeof value === 'string' ? value.replace(' ', 'T') : value;
    const d = new Date(normalized);
    if (isNaN(d.getTime())) return '';
    return d.toISOString().slice(0, 10);
  };

  const deriveStartDateFromEnd = (endDateValue, cycle = 'monthly') => {
    const endDate = new Date(typeof endDateValue === 'string' ? endDateValue.replace(' ', 'T') : endDateValue);
    if (isNaN(endDate.getTime())) return '';
    const start = new Date(endDate);
    if (cycle === 'yearly') {
      start.setFullYear(start.getFullYear() - 1);
    } else {
      start.setMonth(start.getMonth() - 1);
    }
    return start.toISOString().slice(0, 10);
  };

  const openBillingModal = (tenant) => {
    const plan = tenant?.tenant_plans?.[0] || {};
    const safeStartDate = toDateInputValue(plan.subscription_starts_at)
      || deriveStartDateFromEnd(plan.subscription_ends_at, plan.billing_cycle);
    setSelectedTenant(tenant);
    setFormData((prev) => ({
      ...prev,
      planName: plan.plan_name || '',
      planPrice: plan.plan_price || '',
      billingCycle: plan.billing_cycle || 'monthly',
      planStatus: plan.status || 'active',
      subscriptionStartsAt: safeStartDate
    }));
    setShowBillingModal(true);
  };

  const openEditModal = (tenant) => {
    setSelectedTenant(tenant);
    setFormData({
      name: tenant.name || '',
      slug: tenant.slug || '',
      email: tenant.email || '',
      phone: tenant.phone || '',
      notes: tenant.notes || '',
      is_active: tenant.is_active !== false,
      maxStaffLogins: tenant.max_staff_logins || ''
    });
    setShowEditModal(true);
  };

  const openNotesModal = (tenant) => {
    setSelectedTenant(tenant);
    setFormData((prev) => ({
      ...prev,
      notes: tenant.notes || ''
    }));
    setShowNotesModal(true);
  };

  const handleSaveNotes = async () => {
    try {
      if (!selectedTenant) return;

      await api.put(`/api/owner/tenants/${selectedTenant.id}`, {
        notes: formData.notes
      });

      await alert('Notes updated successfully!', 'Success', 'success');
      setShowNotesModal(false);
      fetchTenants();
    } catch (error) {
      await alert(error.response?.data?.error || 'Failed to update notes', 'Error', 'error');
    }
  };

  const openModulesModal = (tenant) => {
    setSelectedTenant(tenant);
    const enabledModules = (tenant.tenant_modules || [])
      .filter(m => m.is_enabled)
      .map(m => m.module_name);
    setFormData({
      ...formData,
      modules: enabledModules
    });
    setShowModulesModal(true);
  };

  const handleAccessTenant = (tenant) => {
    localStorage.setItem('ownerTenantId', tenant.id);
    localStorage.setItem('ownerTenantName', tenant.name || 'Tenant');
    navigate('/restaurant/dashboard');
  };

  const toggleModule = (moduleName) => {
    setFormData(prev => ({
      ...prev,
      modules: prev.modules.includes(moduleName)
        ? prev.modules.filter(m => m !== moduleName)
        : [...prev.modules, moduleName]
    }));
  };

  const filteredTenants = tenants.filter(tenant => {
    const matchesSearch = 
      tenant.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      tenant.slug?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      tenant.email?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesFilter = 
      filterStatus === 'all' ||
      (filterStatus === 'active' && tenant.is_active) ||
      (filterStatus === 'inactive' && !tenant.is_active);

    return matchesSearch && matchesFilter;
  });

  return (
    <Layout>
      <div className="p-8 space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Platform Owner Dashboard</h1>
          <p className="text-gray-600">Manage all tenants and their modules</p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="border-0 bg-white shadow-sm rounded-lg">
            <div className="p-6">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-gray-600">Total Tenants</span>
                <FiBriefcase className="h-5 w-5 text-gray-400" />
              </div>
              <div className="text-3xl font-bold text-gray-900">{stats.totalTenants}</div>
              <p className="text-xs text-gray-500 mt-1">Active organizations</p>
            </div>
          </div>

          <div className="border-0 bg-white shadow-sm rounded-lg">
            <div className="p-6">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-gray-600">Active Tenants</span>
                <FiTrendingUp className="h-5 w-5 text-gray-400" />
              </div>
              <div className="text-3xl font-bold text-gray-900">{stats.activeTenants}</div>
              <p className="text-xs text-gray-500 mt-1">Currently active</p>
            </div>
          </div>

          <div className="border-0 bg-white shadow-sm rounded-lg">
            <div className="p-6">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-gray-600">Total Users</span>
                <FiUsers className="h-5 w-5 text-gray-400" />
              </div>
              <div className="text-3xl font-bold text-gray-900">{stats.totalUsers}</div>
              <p className="text-xs text-gray-500 mt-1">Across all tenants</p>
            </div>
          </div>

          <div className="border-0 bg-white shadow-sm rounded-lg">
            <div className="p-6">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-gray-600">Monthly Revenue</span>
                <FiDollarSign className="h-5 w-5 text-gray-400" />
              </div>
              <div className="text-3xl font-bold text-gray-900">
                ₹{parseFloat(stats.monthlyRevenue || 0).toLocaleString('en-IN')}
              </div>
              <p className="text-xs text-gray-500 mt-1">This month</p>
            </div>
          </div>
        </div>

        {/* Billing Attention */}
        <div className="border-0 bg-white shadow-sm rounded-lg">
          <div className="p-6 border-b border-gray-200 flex items-center justify-between gap-3 flex-wrap">
            <div>
              <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <FiAlertTriangle className="text-amber-600" /> Billing Needs Attention
              </h2>
              <p className="text-sm text-gray-600">Tenants nearing renewal or overdue</p>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600">Threshold</span>
              <select
                value={attentionDays}
                onChange={(e) => setAttentionDays(parseInt(e.target.value, 10) || 5)}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
              >
                <option value={3}>3 days</option>
                <option value={5}>5 days</option>
                <option value={7}>7 days</option>
                <option value={10}>10 days</option>
                <option value={15}>15 days</option>
              </select>
            </div>
          </div>
          <div className="p-6">
            {billingAttention.length === 0 ? (
              <p className="text-sm text-gray-500">No renewals in the selected attention window.</p>
            ) : (
              <div className="space-y-3">
                {billingAttention.map((item) => (
                  <div key={`${item.tenantId}-${item.subscriptionEndsAt}`} className="p-3 rounded-lg border border-gray-200 bg-gray-50 flex items-center justify-between gap-3">
                    <div>
                      <div className="font-semibold text-gray-900">{item.tenantName}</div>
                      <div className="text-xs text-gray-600 flex items-center gap-3 mt-1">
                        <span>{item.planName || 'Plan'}</span>
                        <span>Ends: {new Date(item.subscriptionEndsAt).toLocaleDateString('en-IN')}</span>
                        <span>₹{parseFloat(item.planPrice || 0).toLocaleString('en-IN')}/{item.billingCycle}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`px-2 py-1 rounded text-xs font-semibold ${
                        item.isOverdue ? 'bg-red-100 text-red-700' : item.daysLeft <= 2 ? 'bg-amber-100 text-amber-700' : 'bg-yellow-100 text-yellow-700'
                      }`}>
                        {item.isOverdue ? `Overdue ${Math.abs(item.daysLeft)}d` : `${item.daysLeft}d left`}
                      </span>
                      <button
                        onClick={() => {
                          const tenant = tenants.find((t) => t.id === item.tenantId);
                          if (tenant) openBillingModal(tenant);
                        }}
                        className="px-3 py-1.5 bg-gray-900 text-white rounded-lg text-xs hover:bg-gray-800"
                      >
                        Update Billing
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Tenants List */}
        <div className="border-0 bg-white shadow-sm rounded-lg">
          <div className="border-0">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-gray-900">All Tenants</h2>
                <button
                  onClick={() => {
                    resetForm();
                    setShowCreateModal(true);
                  }}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors text-sm font-medium"
                >
                  <FiPlus /> Create New Client
                </button>
              </div>
            </div>
            <div className="p-6">
              {/* Search and Filter */}
              <div className="mb-6 flex flex-col sm:flex-row gap-4">
                <div className="flex-1 relative">
                  <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search tenants..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <FiFilter className="text-gray-400" />
                  <select
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                    className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                  >
                    <option value="all">All Status</option>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
              </div>

              {loading ? (
                <div className="text-center py-8 text-gray-600">Loading tenants...</div>
              ) : filteredTenants.length > 0 ? (
                <div className="space-y-4">
                  {filteredTenants.map((tenant) => {
                    const plan = tenant.tenant_plans?.[0];
                    const enabledModules = (tenant.tenant_modules || []).filter(m => m.is_enabled);
                    const userCount = tenant.users?.length || 0;

                    return (
                      <div
                        key={tenant.id}
                        className="flex items-center justify-between p-4 border-0 bg-white rounded-lg hover:bg-gray-50 transition-colors shadow-sm"
                      >
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <FiBriefcase className="h-5 w-5 text-gray-400" />
                            <div>
                              <h3 className="font-semibold text-gray-900">{tenant.name}</h3>
                              <p className="text-sm text-gray-500">{tenant.email || tenant.slug || 'No email'}</p>
                              {tenant.notes && (
                                <p className="text-xs text-gray-500 mt-1 line-clamp-1">Notes: {tenant.notes}</p>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-4 mt-2 text-xs text-gray-600">
                            {plan && (
                              <span className="flex items-center gap-1">
                                <FiDollarSign className="h-3 w-3" />
                                {plan.plan_name} - ₹{parseFloat(plan.plan_price || 0).toLocaleString('en-IN')}/{plan.billing_cycle}
                              </span>
                            )}
                            <span className="flex items-center gap-1">
                              <FiUsers className="h-3 w-3" />
                              {userCount} users
                            </span>
                            <span className="flex items-center gap-1">
                              <FiUserCheck className="h-3 w-3" />
                              {tenant.max_staff_logins || 0} max staff
                            </span>
                            <span className="flex items-center gap-1">
                              <FiShoppingBag className="h-3 w-3" />
                              {enabledModules.length} modules
                            </span>
                            <span className={`px-2 py-1 rounded text-xs font-medium ${
                              tenant.is_active 
                                ? 'bg-green-100 text-green-800' 
                                : 'bg-red-100 text-red-800'
                            }`}>
                              {tenant.is_active ? 'Active' : 'Inactive'}
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => openBillingModal(tenant)}
                            className="px-3 py-1.5 text-sm text-amber-700 hover:text-amber-900 hover:bg-amber-50 rounded transition-colors"
                            title="Manage Billing"
                          >
                            <FiCalendar />
                          </button>
                          <button
                            onClick={() => openNotesModal(tenant)}
                            className="px-3 py-1.5 text-sm text-blue-700 hover:text-blue-900 hover:bg-blue-50 rounded transition-colors"
                            title="Tenant Notes"
                          >
                            <FiFileText />
                          </button>
                          <button
                            onClick={() => openEditModal(tenant)}
                            className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded transition-colors"
                            title="Edit Tenant"
                          >
                            <FiEdit />
                          </button>
                          <button
                            onClick={() => handleAccessTenant(tenant)}
                            className="px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors text-sm font-medium flex items-center gap-2"
                            title="Access Tenant Dashboard"
                          >
                            <FiEye className="h-4 w-4" />
                            Access
                          </button>
                          <button
                            onClick={() => navigate(`/owner/tenants/${tenant.id}`)}
                            className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded transition-colors"
                            title="Manage Tenant"
                          >
                            Manage
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-sm text-gray-500 text-center py-8">No tenants found</p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Create Tenant Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-2xl font-bold text-gray-900">Create New Client</h2>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Tenant Name *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                    placeholder="Restaurant Name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Slug *</label>
                  <input
                    type="text"
                    value={formData.slug}
                    onChange={(e) => setFormData({ ...formData, slug: e.target.value.toLowerCase().replace(/\s+/g, '-') })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                    placeholder="restaurant-slug"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Email</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                    placeholder="restaurant@example.com"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Phone</label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                    placeholder="+91 1234567890"
                  />
                </div>
              </div>

              <div className="border-t border-gray-200 pt-4">
                <h3 className="font-semibold text-gray-900 mb-4">Admin User</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Admin Name *</label>
                    <input
                      type="text"
                      value={formData.adminName}
                      onChange={(e) => setFormData({ ...formData, adminName: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                      placeholder="Admin Name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Admin Email *</label>
                    <input
                      type="email"
                      value={formData.adminEmail}
                      onChange={(e) => setFormData({ ...formData, adminEmail: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                      placeholder="admin@restaurant.com"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Admin Password *</label>
                    <input
                      type="password"
                      value={formData.adminPassword}
                      onChange={(e) => setFormData({ ...formData, adminPassword: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                      placeholder="Password"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Max Staff Logins *</label>
                    <input
                      type="number"
                      min="0"
                      value={formData.maxStaffLogins}
                      onChange={(e) => setFormData({ ...formData, maxStaffLogins: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                      placeholder="e.g., 5, 10, 20"
                    />
                    <p className="text-xs text-gray-500 mt-1">Maximum number of staff logins this admin can create</p>
                  </div>
                </div>
              </div>

              <div className="border-t border-gray-200 pt-4">
                <h3 className="font-semibold text-gray-900 mb-4">Plan & Modules</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Plan Name</label>
                    <input
                      type="text"
                      value={formData.planName}
                      onChange={(e) => setFormData({ ...formData, planName: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                      placeholder="Basic / Premium / Enterprise"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Price (₹)</label>
                    <input
                      type="number"
                      value={formData.planPrice}
                      onChange={(e) => setFormData({ ...formData, planPrice: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                      placeholder="0"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Billing Cycle</label>
                    <select
                      value={formData.billingCycle}
                      onChange={(e) => setFormData({ ...formData, billingCycle: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                    >
                      <option value="monthly">Monthly</option>
                      <option value="yearly">Yearly</option>
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Subscription Start (Optional)</label>
                    <input
                      type="date"
                      value={formData.subscriptionStartsAt || ''}
                      onChange={(e) => setFormData({ ...formData, subscriptionStartsAt: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                    />
                  </div>
                </div>

                <div className="mt-4">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Enable Modules</label>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {availableModules.map((module) => (
                      <label key={module} className="flex items-center gap-2 p-2 border border-gray-200 rounded cursor-pointer hover:bg-gray-50">
                        <input
                          type="checkbox"
                          checked={formData.modules.includes(module)}
                          onChange={() => toggleModule(module)}
                          className="rounded border-gray-300 text-gray-900 focus:ring-gray-900"
                        />
                        <span className="text-sm text-gray-700 capitalize">{module.replace('_', ' ')}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            </div>
            <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  resetForm();
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateTenant}
                className="px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors"
              >
                Create Client
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Tenant Modal */}
      {showEditModal && selectedTenant && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-lg w-full">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-2xl font-bold text-gray-900">Edit Tenant</h2>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Slug *</label>
                <input
                  type="text"
                  value={formData.slug}
                  onChange={(e) => setFormData({ ...formData, slug: e.target.value.toLowerCase().replace(/\s+/g, '-') })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Email</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Phone</label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Notes</label>
                <textarea
                  value={formData.notes || ''}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                  placeholder="Internal owner notes for this tenant"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Max Staff Logins *</label>
                <input
                  type="number"
                  min="0"
                  value={formData.maxStaffLogins}
                  onChange={(e) => setFormData({ ...formData, maxStaffLogins: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                  placeholder="e.g., 5, 10, 20"
                />
                <p className="text-xs text-gray-500 mt-1">Maximum number of staff logins this admin can create</p>
              </div>
              <div>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                    className="rounded border-gray-300 text-gray-900 focus:ring-gray-900"
                  />
                  <span className="text-sm text-gray-700">Active</span>
                </label>
              </div>
            </div>
            <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowEditModal(false);
                  resetForm();
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleUpdateTenant}
                className="px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors"
              >
                Update
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modules Modal */}
      {showModulesModal && selectedTenant && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-2xl font-bold text-gray-900">Manage Modules - {selectedTenant.name}</h2>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {availableModules.map((module) => (
                  <label key={module} className="flex items-center gap-2 p-3 border-2 border-gray-200 rounded-lg cursor-pointer hover:border-gray-900 transition-colors">
                    <input
                      type="checkbox"
                      checked={formData.modules.includes(module)}
                      onChange={() => toggleModule(module)}
                      className="rounded border-gray-300 text-gray-900 focus:ring-gray-900"
                    />
                    <span className="text-sm font-medium text-gray-700 capitalize">{module.replace('_', ' ')}</span>
                  </label>
                ))}
              </div>
            </div>
            <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowModulesModal(false);
                  resetForm();
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleUpdateModules}
                className="px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors"
              >
                Save Modules
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Billing Modal */}
      {showBillingModal && selectedTenant && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-lg w-full">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-2xl font-bold text-gray-900">Billing - {selectedTenant.name}</h2>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Plan Name</label>
                <input
                  type="text"
                  value={formData.planName}
                  onChange={(e) => setFormData({ ...formData, planName: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Plan Price (₹)</label>
                  <input
                    type="number"
                    value={formData.planPrice}
                    onChange={(e) => setFormData({ ...formData, planPrice: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Cycle</label>
                  <select
                    value={formData.billingCycle}
                    onChange={(e) => setFormData({ ...formData, billingCycle: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  >
                    <option value="monthly">Monthly</option>
                    <option value="yearly">Yearly</option>
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Subscription Start</label>
                  <input
                    type="date"
                    value={formData.subscriptionStartsAt || ''}
                    onChange={(e) => setFormData({ ...formData, subscriptionStartsAt: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Subscription End</label>
                  <div className="w-full px-4 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-500 text-sm">
                    Auto-calculated from start date and billing cycle
                  </div>
                </div>
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Status</label>
                <select
                  value={formData.planStatus || 'active'}
                  onChange={(e) => setFormData({ ...formData, planStatus: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                >
                  <option value="active">Active</option>
                  <option value="trial">Trial</option>
                  <option value="cancelled">Cancelled</option>
                  <option value="suspended">Suspended</option>
                </select>
              </div>
            </div>
            <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowBillingModal(false);
                  resetForm();
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleUpdatePlan}
                className="px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800"
              >
                Save Billing
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Notes Modal */}
      {showNotesModal && selectedTenant && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-lg w-full">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-2xl font-bold text-gray-900">Tenant Notes - {selectedTenant.name}</h2>
            </div>
            <div className="p-6 space-y-4">
              <textarea
                value={formData.notes || ''}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                rows={8}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                placeholder="Add private owner notes for this tenant..."
              />
            </div>
            <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => setShowNotesModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveNotes}
                className="px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800"
              >
                Save Notes
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}

export default OwnerDashboard;
